import { evaluateFootprintRules, type FootprintPolicy, type FootprintRule, type PropFrameInfo } from "daedalus-core";

/**
 * OdysseyTales's prop footprint policy (ADR-040 step 2, ADR-052): every prop blocks the ground its art
 * stands on, derived from the pixels, except groups that are decoration, shadows, foliage without a trunk,
 * things meant to be walked over, or parts drawn only for sorting. The policy is an ordered rule list the
 * contract publishes, so the editor, the compiler and the asset build read one table and a pack whose art
 * needs another band is a contract edit. Gameplay constants; tune without a new ADR.
 */

const WALKABLE_WORDS = ["shadow", "detail", "grass tufts", "vegetation", "carpet", "rug", "bridge", "for z ordering", "only foliage", "roof", "crops-", "manure", "sword on", "woodplank", "bone", "dragon - grass", "stairs", "flag", "seed bags", "seed boxes", "puddle"];

/**
 * The tileset id prefix of the Village Interiors pack, whose words below hold for that pack alone: they are
 * too plain to trust elsewhere, where a window or a banner stands on the ground. A rule can only ask whether
 * a tileset id contains a text, ignoring case (`FootprintRuleMatch.tilesetContains`), so everything here that
 * reads the pack from a tileset id reads it that way.
 */
const INTERIORS_PACK = "village-interiors:";

/**
 * Groups of the Village Interiors pack that block nothing: what hangs on a wall or from the ceiling (windows,
 * curtains, banners, shields, paintings, mounted heads, the chandelier), the light a window casts on the
 * floor (`sunlight`, the one group the pack's table records for every such patch, whatever window or door its
 * file is named after), the flame and smoke drawn over a candlestick, the flame of a torch, which the pack draws
 * without the torch that holds it (`torch`), the shards a broken pot leaves, and the shelves fixed to a wall
 * (`shelves`, the coordinator's ruling of 2026-10-05: they sit over wall cells a builder paints solid).
 * Furniture, doors, pots, plants and everything else of the pack blocks the ground its art stands on.
 */
const INTERIORS_WALKABLE_WORDS = ["sunlight", "windows", "curtains", "banner", "decor_shields", "decors_paintings", "decor_animals", "chandelier", "candle_and_torch", "torch", "breakable-pots-anim", "shelves"];

/**
 * Names of the Village Interiors pack that block nothing though their group does: the cobwebs (`Slice_0` to
 * `Slice_68`), which share the group `Slice` with three counters (`Slice 4`, `Slice 5`, `Slice 45`), and the
 * swords and the axe hung on a wall (`decors_weapon2_0` to `_6`, the coordinator's ruling of 2026-10-05), which
 * share the group `decors_weapon2` with three weapon racks that stand on the floor (`_7` to `_9`).
 */
const INTERIORS_WALKABLE_NAMES = ["slice_", "decors_weapon2_0", "decors_weapon2_1", "decors_weapon2_2", "decors_weapon2_3", "decors_weapon2_4", "decors_weapon2_5", "decors_weapon2_6"];

/**
 * The footprint policy as rules, in order: a walkable group leaves the prop without collision, and
 * everything else derives its footprint from the art.
 */
export const PROP_FOOTPRINT_RULES: readonly FootprintRule[] = [
  ...WALKABLE_WORDS.map((word): FootprintRule => ({ match: { groupContains: word }, policy: "none" })),
  ...INTERIORS_WALKABLE_WORDS.map((word): FootprintRule => ({ match: { tilesetContains: INTERIORS_PACK, groupContains: word }, policy: "none" })),
  ...INTERIORS_WALKABLE_NAMES.map((word): FootprintRule => ({ match: { tilesetContains: INTERIORS_PACK, nameContains: word }, policy: "none" })),
  { match: {}, policy: "auto" },
];

/**
 * The footprint policy for a prop frame, by its group name.
 *
 * @param frame The frame.
 * @returns `none` for walkable decoration, else `auto`.
 */
export function propFootprintPolicy(frame: PropFrameInfo): FootprintPolicy {
  return evaluateFootprintRules(PROP_FOOTPRINT_RULES, frame);
}

const GROUND_WORDS = ["path", "carpet", "rug", "shadow", "puddle", "bridge", "for z ordering", "floor"];

/**
 * Groups of the Village Interiors pack that are ground art: the light a window casts on the floor.
 */
const INTERIORS_GROUND_WORDS = ["sunlight"];

/**
 * Whether a prop frame is flat ground art that draws under every character rather than sorting by row
 * (ADR-040): paths, carpets, shadows and bridge decks, and the light a Village Interiors window casts on the
 * floor.
 *
 * @param frame The frame; its tileset id holds its pack's id, which is read as the footprint rules read it.
 * @returns True for ground art.
 */
export function propIsGround(frame: PropFrameInfo): boolean {
  const group = frame.group.toLowerCase();
  return GROUND_WORDS.some((word) => group.includes(word)) || (frame.tileset.toLowerCase().includes(INTERIORS_PACK) && INTERIORS_GROUND_WORDS.some((word) => group.includes(word)));
}

/**
 * Words naming the groups of a building's separate parts: its walls, its interior or floor, its porch, its door and
 * the cabin's chimney (`cabin-chimney`, which the roofs "with chimney" do not match). A group holding one of them is
 * a building whatever else its name holds, so `porch-stairs_5-fence` is a porch before it is a fence.
 */
const BUILDING_PART_WORDS = ["cabin - interior", "bot section", "door", "porch", "cabin-chimney", "blacksmith wall", "halfsized walls", "wall pack"];

/**
 * Words naming what is called after a building and is not one, read before the building words below: ground art
 * and the strips that blend a building into grass or sand, signs, flags, smoke, fences and ropes, a campfire, a
 * statue, the cabin's furnishings (`cabin interior - bed`; the interior itself is `cabin - interior`), the smith's
 * loose props and water trough, the cemetery's crypt fences and ornaments, the cabin's roofs, which are placed as
 * Roofs and open by their area instead (ADR-040's amendment of 2026-09-24), and a cabinet, which is furniture and
 * holds the word `cabin`.
 */
const NOT_BUILDING_WORDS = ["cabinet", "shadow", "sign", "flag", "smoke", "fence", "rope", "campfire", "statue", "cabin interior", "top section", "cabin-roof", "cabin-grass", "cabin-sand", "lighthouse grass", "lighthouse sand", "blacksmith props", "water deposit", "crypt details", "crypt - "];

/**
 * Words naming a whole building drawn as one image, or as a few large ones, in any pack: houses (premade houses,
 * deck houses and the lighthouse among them), cabins, smithies and shops, a chapel, a temple, a ruined house, mills,
 * fortress and stronghold walls and towers, watchtowers and the orc tents.
 */
const BUILDING_WORDS = ["house", "cabin", "blacksmith", "shop", "chapel", "ancient-temple", "abandoned", "windmill", "watermill", "fortress", "stronghold", "watchtower", "deck-storage", "orc-tents"];

/**
 * What a group's name starts with when it is a building: a tent a character could live in (`tent`,
 * `tent 1 - small - on grass`). A carriage's tent, a vendor's tent and a capsule's "content" do not start with it.
 */
const BUILDING_PREFIXES = ["tent"];

/**
 * Words that name a building in one pack only, by pack id, since they are too plain to trust elsewhere: The
 * Village's chimneys, windows, beam supports and roof tufts, drawn onto its houses; the cemetery's crypts (the crypt
 * pack is a dungeon's inside and has none); Ancient Ruins' small tower; the volcano's towers; the sea pack's tower monument, a
 * doored tower (the author's ruling of 2026-10-05).
 */
const PACK_BUILDING_WORDS: Readonly<Record<string, readonly string[]>> = {
  "the-village": ["chimney-", "windows-", "supports", "tufts-of-grass-roof"],
  cemetery: ["crypt", "cypt"],
  "ancient-ruins": ["generic structure"],
  volcano: ["towers"],
  "sea-adventures": ["islands-grass-tower-monument"],
};

/**
 * A group that fades though a word in its name says building, named whole (in lower case) with its pack, so no other
 * group and no other pack is touched; `walls` lists the frames of it that are buildings all the same, by sheet and
 * tile, and never fade.
 */
interface FadingGroup {
  readonly pack: string;
  readonly group: string;
  readonly walls?: readonly { readonly tileset: string; readonly tiles: readonly number[] }[];
}

/**
 * The groups read by their art and not their name, before every word list: Grassland 2.0's `doorway - entrance -
 * gateway` is a free-standing gate of two posts and a beam; the cemetery's `little house - 1 and` is the fence rail
 * of its little houses; the sea pack's `tent` is an open tarp slung from a mast (the author's ruling of 2026-10-05);
 * the cemetery's `Abandoned Structures Elements` is the loose pieces of its ruins, on a night sheet and a day sheet.
 * Of those pieces one rule decides, the same on both sheets: a piece that has a floor or encloses a space (a wall
 * with its floor, a corner with its floor, the whole house) stays a building, and a free-standing wall without a
 * floor (a wall section, the doorway wall, the arch, a low remnant) fades with the logs, posts, crosses and rubble.
 */
const FADING_GROUPS: readonly FadingGroup[] = [
  { pack: "grass-land-2.0", group: "doorway - entrance - gateway" },
  { pack: "cemetery", group: "little house - 1 and" },
  { pack: "sea-adventures", group: "tent" },
  {
    pack: "cemetery",
    group: "abandoned structures elements",
    walls: [
      { tileset: "cemetery:atlas-props-sprites", tiles: [716, 722, 724, 730, 733, 746, 747, 749, 765] },
      { tileset: "cemetery:generated-static-props", tiles: [14, 20, 23, 38, 39, 41, 62, 64] },
    ],
  },
];

/**
 * Frames that fade though their group is a building, by pack, group (in lower case), sheet and tile: what is cut into
 * the cemetery's ruined-house groups and fades among `Abandoned Structures Elements` by that group's rule. The loose
 * timbers (a fallen fence of posts and rails, a plank, corner posts, a hanging sign, a timber door frame, a log) and
 * the free-standing walls without a floor (the doorway wall and the two halves of the arch). The walls with floors,
 * the floors and the rubble of those groups stay buildings.
 */
const LOOSE_PIECES: readonly { readonly pack: string; readonly group: string; readonly tileset: string; readonly tiles: readonly number[] }[] = [
  { pack: "cemetery", group: "abandoned-structures-parts-e3", tileset: "cemetery:generated-static-props", tiles: [81, 83] },
  { pack: "cemetery", group: "abandoned-structures-parts-e5", tileset: "cemetery:generated-static-props", tiles: [88, 89, 90] },
  { pack: "cemetery", group: "abandoned-structures-parts-e2", tileset: "cemetery:generated-static-props", tiles: [80] },
  { pack: "cemetery", group: "abandoned-structures-parts-e8", tileset: "cemetery:generated-static-props", tiles: [102, 103, 104] },
  { pack: "cemetery", group: "abandoned-structures-day-light-parts-e1", tileset: "cemetery:generated-static-props", tiles: [117] },
  { pack: "cemetery", group: "abandoned-structures-day-light-parts-e3", tileset: "cemetery:generated-static-props", tiles: [119, 121] },
  { pack: "cemetery", group: "abandoned-structures-day-light-parts-e5", tileset: "cemetery:generated-static-props", tiles: [127, 128, 129] },
  { pack: "cemetery", group: "abandoned-structures-day-light-parts-e8", tileset: "cemetery:generated-static-props", tiles: [139, 140] },
];

/**
 * Whether a prop frame is a building or a part of one, which the occluder fade and the cone of sight never dim (the
 * author's rulings of 2026-10-03 and 2026-10-04, ADR-040's amendments of those days): a building never fades,
 * whether it is drawn as separate parts or as one image. Trees, rocks, wells, fountains, stalls and street furniture
 * keep fading. This is the default a frame's group gives; a frame's override in the library (`fades`) wins over it.
 * Every group of every pack is listed with its answer in ADR-040's appendix, which a test holds to this function.
 *
 * @param frame The frame; its tileset id starts with its pack's id.
 * @returns True for a building or a building part.
 */
export function propIsBuilding(frame: PropFrameInfo): boolean {
  const group = frame.group.toLowerCase();
  const separator = frame.tileset.indexOf(":");
  const pack = separator > 0 ? frame.tileset.slice(0, separator) : "";
  const fading = FADING_GROUPS.find((entry) => entry.pack === pack && entry.group === group);
  if (fading !== undefined) {
    return fading.walls?.some((sheet) => sheet.tileset === frame.tileset && sheet.tiles.includes(frame.tile)) ?? false;
  }
  if (LOOSE_PIECES.some((entry) => entry.pack === pack && entry.group === group && entry.tileset === frame.tileset && entry.tiles.includes(frame.tile))) {
    return false;
  }
  if (BUILDING_PART_WORDS.some((word) => group.includes(word))) {
    return true;
  }
  if (NOT_BUILDING_WORDS.some((word) => group.includes(word))) {
    return false;
  }
  const packWords = Object.hasOwn(PACK_BUILDING_WORDS, pack) ? PACK_BUILDING_WORDS[pack] : undefined;
  return BUILDING_PREFIXES.some((word) => group.startsWith(word)) || BUILDING_WORDS.some((word) => group.includes(word)) || (packWords !== undefined && packWords.some((word) => group.includes(word)));
}
